import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { EditorView } from '@codemirror/view';
import { tags as t } from '@lezer/highlight';

/**
 * Markdown styled live, in the text itself — headings, bold, links, code and
 * `- [ ]` checkboxes are visibly distinct without a separate preview pane.
 *
 * The syntax markers (`##`, `**`, `-`) stay on screen in `text-muted`. Hiding
 * them would make the cursor jump over invisible characters; muting them keeps
 * the text honest and still lets the heading read as a heading.
 */
const markdown = HighlightStyle.define([
  { tag: t.heading1, fontSize: 'var(--text-h1)', fontWeight: '600', lineHeight: 'var(--leading-tight)' },
  { tag: t.heading2, fontSize: 'var(--text-h2)', fontWeight: '600', lineHeight: 'var(--leading-tight)' },
  { tag: t.heading3, fontSize: 'var(--text-h3)', fontWeight: '600', lineHeight: 'var(--leading-tight)' },
  { tag: [t.heading4, t.heading5, t.heading6], fontWeight: '600' },

  { tag: t.strong, fontWeight: '600', color: 'var(--text-primary)' },
  { tag: t.emphasis, fontStyle: 'italic' },
  { tag: t.strikethrough, textDecoration: 'line-through', color: 'var(--text-muted)' },

  { tag: [t.link, t.url], color: 'var(--accent)', textDecoration: 'none' },

  {
    tag: [t.monospace],
    fontFamily: 'var(--font-mono)',
    fontSize: '14px',
    background: 'var(--bg-surface-raised)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--r-chip)',
    padding: '1px 5px',
  },

  { tag: t.quote, color: 'var(--text-secondary)' },
  { tag: t.atom, color: 'var(--accent)' }, // `- [x]` task markers
  { tag: t.contentSeparator, color: 'var(--border-default)' },

  // Every structural marker: ##, **, -, >, ```, the [ ] brackets.
  { tag: [t.processingInstruction, t.meta], color: 'var(--text-muted)', fontWeight: '400' },
]);

const theme = EditorView.theme(
  {
    '&': {
      color: 'var(--text-primary)',
      backgroundColor: 'transparent',
      fontSize: 'var(--text-body)',
      flex: '1',
    },
    '.cm-scroller': {
      fontFamily: 'var(--font-ui)',
      lineHeight: 'var(--leading-body)',
      overflowY: 'auto',
      overscrollBehavior: 'contain',
    },
    '.cm-content': {
      padding: '0',
      caretColor: 'var(--accent)',
      maxWidth: 'var(--editor-max)',
    },
    '.cm-line': { padding: '0' },
    '&.cm-focused': { outline: 'none' },
    '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--accent)', borderLeftWidth: '2px' },
    '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
      backgroundColor: 'var(--accent-deep)',
    },
    '.cm-placeholder': { color: 'var(--text-muted)' },
    '.cm-gutters': { display: 'none' },
    // Fenced code blocks read as one surface rather than per-token chips.
    '.cm-line:has(.cm-codeblock)': { fontFamily: 'var(--font-mono)' },
  },
  { dark: true },
);

export const editorExtensions = [theme, syntaxHighlighting(markdown)];
